"use client"

import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { sendOtp, clearSendOtp } from "../../store/slices/sendOtpSlice"
import { verifyOtp, clearVerifyOtp } from "../../store/slices/verifyOtpSlice"
import { registerWithPassword } from "../../store/slices/registerPasswordSlice"
import { setCredentials } from "../../store/slices/authSlice"
import OtpPopup from "./OtpPopup"
import SuccessPopup from "./SuccessPopup"
import {
  getPasswordRequirementStatus,
  getPasswordChangeValidationErrors,
  getConfirmPasswordMismatchError,
  validatePasswordPair,
  isNewPasswordSubmittable,
  PASSWORD_REQUIREMENT_UI,
  PASSWORD_EASY_PATTERNS_MESSAGE,
} from "../../lib/utils/passwordUtils"
import { generateRecaptchaToken, RECAPTCHA_SITE_KEY } from "../../lib/utils/recaptcha"
import { clearVerifyOtpError } from "../../store/slices/verifyOtpSlice"
import { getAuthMessage } from "../../lib/constants/authStatusCodes"
import { clearUserMedias, updateUserProfile } from "../../store/slices/userSlice"
import "./SignupForm.css"
import PasswordEyeIcon from "../../lib/utils/PasswordEyeIcon"

const isBusinessEmail = (email) => {
  if (typeof email !== "string" || !email.trim()) return false;

  const normalizedEmail = email.trim().toLowerCase();

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/;
  if (!EMAIL_REGEX.test(normalizedEmail)) return false;

  const domain = normalizedEmail.split("@")[1];

  const FREE_EMAIL_REGEX = /(^|\.)?(gmail|yahoo|hotmail|outlook|icloud|protonmail|zoho|aol|gmx)\.com$/i;
  if (FREE_EMAIL_REGEX.test(domain)) return false;

  const SUSPICIOUS_REGEX = /(tempmail|disposable|fakeemail)/i;
  if (SUSPICIOUS_REGEX.test(domain)) return false;

  return true;
};

const isValidFullName = (value) => { return value.trim().length >= 2; };


const SignupStepOne = ({ onNext, initialData = {} }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { loading: sendOtpLoading, success: otpSent, error: sendOtpError, data: otpData } = useSelector((state) => state.sendOtp)
  const { loading: verifyOtpLoading, error: verifyOtpError, data: verifyOtpData } = useSelector((state) => state.verifyOtp)

  const {
    loading: registerLoading,
    success: registerSuccess,
    error: registerError,
    token: authToken,
    user: authUser,
  } = useSelector((state) => state.registerPassword)

  const [otpRequested, setOtpRequested] = useState(false)
  const [showOtpFields, setShowOtpFields] = useState(false)
  const [otpInput, setOtpInput] = useState(["", "", "", "", "", ""])
  const [emailError, setEmailError] = useState("")
  const [validationErrors, setValidationErrors] = useState({})
  const [showOtpPopup, setShowOtpPopup] = useState(false)
  const [otpPopupStatus, setOtpPopupStatus] = useState("sent")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showSuccessPopup, setShowSuccessPopup] = useState(false)
  const [isVerified, setIsVerified] = useState(false)
  const [otpTimer, setOtpTimer] = useState(0) // seconds
  const [canResendOtp, setCanResendOtp] = useState(false)
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [showPrivacyModal, setShowPrivacyModal] = useState(false)
  const [otpPopupMessage, setOtpPopupMessage] = useState("")
  const [otpPopupErrorTitle, setOtpPopupErrorTitle] = useState("")

  const [formData, setFormData] = useState({
    fullName: initialData.fullName || "",
    businessEmail: initialData.businessEmail || "",
    password: initialData.password || "",
    confirmPassword: initialData.confirmPassword || "",
  })

  const isEmailValid = isBusinessEmail(formData.businessEmail)

  const passwordRequirements = getPasswordRequirementStatus(
    formData.password,
    formData.fullName,
  )

  const showPasswordFieldError =
    Boolean(validationErrors.password) &&
    validationErrors.password !== PASSWORD_EASY_PATTERNS_MESSAGE
  useEffect(() => {
    if (sendOtpError) {
      const timer = setTimeout(() => {
        dispatch(clearSendOtp())
      }, 10000)

      return () => clearTimeout(timer)
    }
  }, [sendOtpError, dispatch])

  useEffect(() => {
    if (registerSuccess) {
      if (authToken && authUser) {
        dispatch(clearUserMedias())
        dispatch(setCredentials({ token: authToken, user: authUser }))
      }

      setShowSuccessPopup(true)
      setTimeout(() => {
        setShowSuccessPopup(false)
        onNext(formData)
      }, 2500)
    }
  }, [registerSuccess, authToken, authUser, dispatch])

  useEffect(() => {
    if (otpSent && otpData && otpData.success !== false) {
      setOtpPopupMessage(otpData?.message)
      setShowOtpFields(true)
      setOtpRequested(true)
      setOtpInput(["", "", "", "", "", ""])
      setOtpPopupStatus("sent")
      setShowOtpPopup(true)

      // ⏱ Start 1 minute timer
      setOtpTimer(60)
      setCanResendOtp(false)

      const interval = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval)
            setCanResendOtp(true)
            return 0
          }
          return prev - 1
        })
      }, 1000)

      setTimeout(() => {
        setShowOtpPopup(false)
      }, 5000)

      return () => clearInterval(interval)
    }
  }, [otpSent, otpData])

  // Backend can return 200 with success: false (e.g. RESEND_TOO_SOON) — heading from API message, description from auth file
  useEffect(() => {
    if (!sendOtpLoading && otpData && otpData.success === false) {
      const heading = otpData?.message || getAuthMessage(otpData.code) || getAuthMessage("INVALID_OTP")
      const description = getAuthMessage(otpData.code) || getAuthMessage("INVALID_OTP")
      setOtpPopupStatus("error")
      setOtpPopupErrorTitle(heading)
      setOtpPopupMessage(description)
      setShowOtpPopup(true)
      setTimeout(() => setShowOtpPopup(false), 5000)
      dispatch(clearSendOtp())
    }
  }, [sendOtpLoading, otpData, dispatch])

  const handleGenerateOtp = async () => {
    dispatch(clearVerifyOtpError())
    dispatch(clearVerifyOtp())

    try {
      const captchaToken = await generateRecaptchaToken("sendotp")

      dispatch(
        sendOtp({
          email: formData.businessEmail.toLowerCase(),
          full_name: formData.fullName,
          captchaToken,
        }),
      )
        .unwrap()
        .catch((error) => {
          if (error) {
            const code = error?.code ?? error?.message ?? error
            const heading = error?.message || getAuthMessage(code) || getAuthMessage("INVALID_OTP")
            const description = getAuthMessage(code) || getAuthMessage("INVALID_OTP")
            setOtpPopupStatus("error")
            setOtpPopupErrorTitle(heading)
            setOtpPopupMessage(description)
            setShowOtpPopup(true)
            setTimeout(() => {
              setShowOtpPopup(false)
            }, 5000)
          }
        })
    } catch (error) {
      console.error("Failed to generate reCAPTCHA token:", error)
      setOtpPopupStatus("error")
      setOtpPopupErrorTitle("Verification delayed")
      setOtpPopupMessage(
        "Security check timed out. Please check your connection and try again.",
      )
      setShowOtpPopup(true)
      setTimeout(() => {
        setShowOtpPopup(false)
      }, 6000)
    }
  }

  const handleCloseOtpPopup = () => {
    setShowOtpPopup(false)
  }

  const handleChange = (e) => {
    const { name, value } = e.target

    if (name === "fullName") {
      const cleanValue = value.slice(0, 100);

      setFormData((prev) => ({
        ...prev,
        fullName: cleanValue,
      }));

      if (cleanValue.trim().length < 2) {
        setValidationErrors((prev) => ({
          ...prev,
          fullName: "Name must be at least 2 characters",
        }));
      } else {
        setValidationErrors((prev) => {
          const copy = { ...prev };
          delete copy.fullName;
          return copy;
        });
      }

      return;
    }

    if (name === "businessEmail") {
      // ❌ block invalid characters
      const cleanEmail = value.replace(/[^a-zA-Z0-9@._+-]/g, "")

      if (cleanEmail.length > 100) return

      setFormData((prev) => ({
        ...prev,
        businessEmail: cleanEmail,
        ...(otpRequested ? { password: "", confirmPassword: "" } : {}),
      }))

      // ✅ strict validation
      if (cleanEmail) {
        const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/;

        if (!EMAIL_REGEX.test(cleanEmail)) {
          setEmailError("Please enter a valid email address.");
        } else if (!isBusinessEmail(cleanEmail)) {
          setEmailError("Please use a work/business email (no Gmail, Yahoo, etc).");
        } else {
          setEmailError("");
        }
      }

      // 🔁 reset OTP when email changes
      if (otpRequested) {
        setOtpRequested(false)
        setShowOtpFields(false)
        setOtpInput(["", "", "", "", "", ""])
        setIsVerified(false)
        dispatch(clearSendOtp())
        dispatch(clearVerifyOtp())
        setOtpTimer(0)
        setCanResendOtp(false)
      }

      return
    }

    if (name === "password") {

      setFormData((prev) => ({
        ...prev,
        password: value,
      }))
      const newErrors = { ...validationErrors }
      const fieldErrors = getPasswordChangeValidationErrors(
        value,
        formData.fullName,
        formData.confirmPassword,
      )
      if (fieldErrors.password) newErrors.password = fieldErrors.password
      else delete newErrors.password
      if (fieldErrors.confirmPassword) newErrors.confirmPassword = fieldErrors.confirmPassword
      else if (formData.confirmPassword) delete newErrors.confirmPassword

      setValidationErrors(newErrors)
    }

    if (name === "confirmPassword") {

      setFormData((prev) => ({
        ...prev,
        confirmPassword: value,
      }))
      const newErrors = { ...validationErrors }

      const mismatch = getConfirmPasswordMismatchError(formData.password, value)
      if (mismatch) newErrors.confirmPassword = mismatch
      else delete newErrors.confirmPassword

      setValidationErrors(newErrors)
    }
  }

  const handleOtpChange = (e, index) => {
    const value = e.target.value
    if (!/^\d?$/.test(value)) return

    const updatedOtp = [...otpInput]
    updatedOtp[index] = value
    setOtpInput(updatedOtp)

    if (verifyOtpError) {
      dispatch(clearVerifyOtpError())
    }

    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`)
      if (nextInput) nextInput.focus()
    }
  }

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpInput[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`)
      if (prevInput) prevInput.focus()
    }
  }

  const handleVerifyOtp = () => {
    const otp = otpInput.join("")

    dispatch(clearVerifyOtpError())

    dispatch(verifyOtp({ email: formData.businessEmail, otp }))
      .unwrap()
      .then((data) => {
        setIsVerified(true)
        setFormData((prev) => ({ ...prev, password: "", confirmPassword: "" }))

        setTimeout(() => {
          setShowOtpFields(false)
        }, 1200)

        setCanResendOtp(false)
        setOtpTimer(0)
        setOtpPopupStatus("verified")
        setOtpPopupMessage(data?.message)
        setShowOtpPopup(true)
        setTimeout(() => setShowOtpPopup(false), 5000)
      })

      .catch((error) => {
        setIsVerified(false)
        const code = error?.code ?? error?.message ?? error
        const heading = error?.message || getAuthMessage(code) || getAuthMessage("INVALID_OTP")
        const description = getAuthMessage(code) || getAuthMessage("INVALID_OTP")
        setOtpPopupStatus("error")
        setOtpPopupErrorTitle(heading)
        setOtpPopupMessage(description)
        setShowOtpPopup(true)
        setTimeout(() => setShowOtpPopup(false), 5000)
      })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const errors = {}
    let hasError = false

    if (!isValidFullName(formData.fullName)) {
      errors.fullName = "Full name is required";
      hasError = true;
    }

    if (!formData.businessEmail.trim()) {
      errors.businessEmail = "Business email is required"
      hasError = true
    } else if (!isBusinessEmail(formData.businessEmail)) {
      errors.businessEmail = "Please use a work or business email address"
      hasError = true
    }

    if (!isVerified) {
      errors.emailVerification = "Please verify your email with OTP"
      hasError = true
    }

    Object.assign(
      errors,
      validatePasswordPair({
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        fullName: formData.fullName,
      }),
    )
    if (errors.password || errors.confirmPassword) hasError = true

    setValidationErrors(errors)

    if (hasError) {
      const firstErrorField = Object.keys(errors)[0]
      const element = document.getElementById(firstErrorField)
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" })
      }
      return
    }

    generateRecaptchaToken("signup")
      .then((captchaToken) => {
        dispatch(
          registerWithPassword({
            email: formData.businessEmail,
            username: formData.businessEmail,
            fullName: formData.fullName,
            password: formData.password,
            captchaToken,
            captchaSiteKey: RECAPTCHA_SITE_KEY,
          }),
        )

          .unwrap()
          .then((res) => {
            const userId = res?.user?.id || res?.id

            if (!userId) {
              console.error("User ID missing in register response", res)
              return
            }

            dispatch(
              updateUserProfile({
                id: userId,
                fullName: formData.fullName,
              }),
            )
          })
      })
      .catch((error) => {
        console.error("Failed to generate reCAPTCHA token:", error)
        setOtpPopupStatus("error")
        setOtpPopupErrorTitle("Verification delayed")
        setOtpPopupMessage(
          "Security check timed out. Please check your connection and try again.",
        )
        setShowOtpPopup(true)
        setTimeout(() => setShowOtpPopup(false), 6000)
      })

  }

  const isStepOneComplete =
    isValidFullName(formData.fullName) &&
    isBusinessEmail(formData.businessEmail) &&
    isVerified &&
    isNewPasswordSubmittable(formData.password, formData.confirmPassword, formData.fullName) &&
    agreedToTerms

  return (
    <div className="signup-page" role="main" aria-label="Signup Form - Step 1">
      <OtpPopup isVisible={showOtpPopup} onClose={handleCloseOtpPopup} status={otpPopupStatus} message={otpPopupMessage} errorTitle={otpPopupErrorTitle} />
      <SuccessPopup isVisible={showSuccessPopup} />

      {showTermsModal && (
        <div className="terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="terms-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="terms-modal-header">
              <h3>Terms & Conditions</h3>
            </div>
            <div className="terms-modal-body">
              <p>
                By accessing or using our platform, you agree to comply with and be bound by these Terms & Conditions. These terms govern your use of our services and outline the rights and responsibilities of both you and the platform.

                You agree to use the platform only for lawful purposes and in accordance with all applicable laws and regulations. Any misuse, unauthorized access, or activity that disrupts the platform’s functionality may result in suspension or termination of your account.

                All content, features, and services provided on this platform are owned by or licensed to us and are protected by applicable intellectual property laws. You may not copy, modify, distribute, or reproduce any part of the platform without prior written consent.

                We strive to ensure uninterrupted and accurate service; however, we do not guarantee that the platform will always be error-free or available at all times. We reserve the right to modify, suspend, or discontinue any part of the service without prior notice.

                To the fullest extent permitted by law, we shall not be liable for any indirect, incidental, or consequential damages arising from your use of the platform.

                By continuing to use our services, you acknowledge that you have read, understood, and agreed to these Terms & Conditions.
              </p>
            </div>
            <div className="terms-modal-footer">
              <button className="terms-modal-close" onClick={() => setShowTermsModal(false)}>
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}

      {showPrivacyModal && (
        <div className="terms-modal-overlay" onClick={() => setShowPrivacyModal(false)}>
          <div className="terms-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="terms-modal-header">
              <h3>Privacy policy</h3>
            </div>
            <div className="terms-modal-body">
              <p>
                Your privacy is important to us, and we are committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and safeguard your data when you use our platform.

                We collect only the information necessary to provide and improve our services, such as your name, email address, and account-related details. Your information is used solely for account management, communication, service enhancements, and compliance with legal obligations.

                We do not sell, rent, or trade your personal information with third parties. Data may be shared only with trusted service providers or when required by law, and always under strict confidentiality and security measures.

                We implement industry-standard security practices to protect your data from unauthorized access, loss, or misuse. While we take all reasonable steps to safeguard your information, no digital platform can guarantee absolute security.

                You have the right to access, update, or request deletion of your personal data, subject to legal and operational requirements.

                By using our platform, you consent to the collection and use of your information as described in this Privacy Policy.
              </p>
            </div>
            <div className="terms-modal-footer">
              <button className="terms-modal-close" onClick={() => setShowPrivacyModal(false)}>
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="signup-container">
        <div className="signup-box">
          <h2>Start your 14 day free trial and boost your hotel's marketing</h2>
          <form onSubmit={handleSubmit}>
            <label htmlFor="fullName">
              Full Name <span className="required-indicator">*</span>
            </label>
            <input
              id="fullName"
              type="text"
              name="fullName"
              placeholder="Enter your full name"
              value={formData.fullName}
              onChange={handleChange}
              maxLength={100}
              required
              autoComplete="name"
              aria-label="Full Name"
              aria-required="true"
              className={validationErrors.fullName ? "input-error" : ""}
            />
            {validationErrors.fullName && <p className="error-text">{validationErrors.fullName}</p>}

            <label htmlFor="businessEmail">
              Business Email <span className="required-indicator">*</span>
            </label>
            <div className="email-otp-row">
              <input
                id="businessEmail"
                type="email"
                name="businessEmail"
                placeholder="Enter your work/business email"
                value={formData.businessEmail}
                onChange={handleChange}
                maxLength={100}
                required
                autoComplete="email"
                aria-label="Business Email"
                aria-required="true"
                className={emailError || validationErrors.businessEmail ? "input-error" : ""}
              />
              <button
                type="button"
                className="otp-btn"
                onClick={handleGenerateOtp}
                disabled={
                  !isValidFullName(formData.fullName) ||
                  !isEmailValid ||
                  sendOtpLoading ||
                  isVerified ||
                  (otpRequested && !canResendOtp)
                }

              >
                {sendOtpLoading ? "Sending..." : otpRequested ? "Resend OTP" : "Get OTP"}
              </button>

            </div>

            
            {emailError && <p className="error-text">{emailError}</p>}

            {/* existing submit error */}
            {validationErrors.businessEmail && (
              <p className="error-text">{validationErrors.businessEmail}</p>
            )}

            
            {otpRequested && !canResendOtp && !isVerified && (
              <p className="otp-timer-text">
                Resend OTP in <span className="otp-timer">{otpTimer}s</span>
              </p>
            )}


            {sendOtpError && <p className="error-text">{typeof sendOtpError === "object" ? sendOtpError?.message : sendOtpError}</p>}

            <p className="info-text">
              We'll send a verification code to this email.
            </p>

            {showOtpFields && (
              <div className="otp-section" role="group" aria-label="OTP Input Fields">
                <label>Enter Code</label>
                <div className="otp-verifyBox">
                  <div className="otp-inputs">
                    {otpInput.map((digit, index) => (
                      <input
                        key={index}
                        id={`otp-${index}`}
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(e, index)}
                        onKeyDown={(e) => handleOtpKeyDown(e, index)}
                        aria-label={`OTP Digit ${index + 1}`}
                        className="otp-box"
                        disabled={isVerified}
                      />
                    ))}
                  </div>
                  {isVerified && (
                    <p className="success-text">
                      <svg width="24" height="24" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M16.5 8.31039V9.00039C16.4991 10.6177 15.9754 12.1914 15.007 13.4868C14.0386 14.7821 12.6775 15.7297 11.1265 16.1883C9.57557 16.6469 7.91794 16.5918 6.40085 16.0313C4.88376 15.4708 3.58849 14.435 2.70822 13.0782C1.82795 11.7214 1.40984 10.1164 1.51626 8.50262C1.62267 6.88881 2.24791 5.35263 3.29871 4.12319C4.34951 2.89375 5.76959 2.03692 7.34714 1.6805C8.92469 1.32407 10.5752 1.48714 12.0525 2.14539"
                          stroke="#29A155"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M16.5 3L9 10.5075L6.75 8.2575"
                          stroke="#29A155"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Verified
                    </p>
                  )}
                  {!isVerified && (
                    <button
                      type="button"
                      className={`verify-btn ${otpInput.join("").length === 6 ? "active" : "disabled"}`}
                      disabled={otpInput.join("").length !== 6 || verifyOtpLoading}
                      onClick={handleVerifyOtp}
                    >
                      {verifyOtpLoading ? "Verifying..." : "Verify"}
                    </button>
                  )}
                </div>
                {verifyOtpError && <p className="otp-error-message">{typeof verifyOtpError === "object" ? verifyOtpError?.message : verifyOtpError}</p>}
              </div>
            )}

            {isVerified && !showOtpFields && (
              <>
                <label htmlFor="password">
                  Create Password <span className="required-indicator">*</span>
                </label>
                <div className="password-input-wrapper">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter Password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    autoComplete="new-password"
                    aria-label="Create Password"
                    aria-required="true"
                    className={validationErrors.password ? "input-error" : ""}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <PasswordEyeIcon isVisible={showPassword} />
                  </button>
                </div>
                <div className="password-requirements">
                  {PASSWORD_REQUIREMENT_UI.map(({ key, label }) => (
                    <div
                      key={key}
                      className={`requirement ${passwordRequirements[key] ? "met" : ""}`}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M13.3334 4L6.00002 11.3333L2.66669 8"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
                {showPasswordFieldError && (
                  <p className="error-text">{validationErrors.password}</p>
                )}

                <label htmlFor="confirmPassword">
                  Confirm Password <span className="required-indicator">*</span>
                </label>
                <div className="password-input-wrapper">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Re-enter Password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    autoComplete="new-password"
                    aria-label="Confirm Password"
                    aria-required="true"
                    className={validationErrors.confirmPassword ? "input-error" : ""}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    <PasswordEyeIcon isVisible={showConfirmPassword} />
                  </button>
                </div>
                {validationErrors.confirmPassword && <p className="error-text">{validationErrors.confirmPassword}</p>}
              </>
            )}

            <div className="next-step-info" role="status" aria-live="polite">
              {/* <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="9" stroke="#6F42C1" strokeWidth="2" fill="none" />
                <circle cx="10" cy="7" r="1" fill="#6F42C1" />
                <rect x="9.5" y="9" width="1" height="5" fill="#6F42C1" />
              </svg> */}
              <img src="/assets/brand/icon-bulb.png" alt="Bulb" />

              <span>Next step: You'll set up your property details to personalize your experience.</span>
            </div>

            <div className="terms-checkbox-wrapper">
              <label className="terms-checkbox-label">
                <input
                  type="checkbox"
                  className="terms-checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  aria-label="Agree to Terms and Conditions"
                />
                <span className="terms-text">
                  By creating an account, you agree to our{" "}
                  <button type="button" className="terms-link" onClick={() => setShowTermsModal(true)}>
                    Terms & Conditions
                  </button>{" "}
                  and{" "}
                  <button type="button" className="terms-link" onClick={() => setShowPrivacyModal(true)}>
                    Privacy Policy
                  </button>
                  .
                </span>
              </label>
            </div>



            <button
              type="submit"
              className={`signup-btn ${isStepOneComplete ? "active" : ""}`}
              disabled={!isStepOneComplete || registerLoading}
            >
              {registerLoading ? "Activating..." : "Activate Account"}
            </button>

            <p
              className="inv-login-text"
              style={{ textAlign: "center", marginTop: "16px", fontSize: "14px", color: "#666" }}
            >
              Already have a SoHo account?
              <span
                className="inv-login-link"
                onClick={() => navigate("/login")}
                style={{ color: "#AC1FAE", cursor: "pointer", marginLeft: "4px", fontWeight: "500" }}
              >
                Log in
              </span>
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}

export default SignupStepOne
